import React, { useContext, useMemo, useState } from "react";
import _ from "lodash/fp";
import { Dialog, GridGenerator } from "@actinc/dls/components";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
} from "@mui/material";
import { useForm } from "react-hook-form";

import { ControlledFormSelect, DataGrid } from "../";

import { StandardsContext } from "../../standardsProvider";

const StandardsDialog = ({
  action,
  onClose,
  defaultValues = {},
  open = false,
  selectedStandards,
  selectedStandard,
}) => {
  const standardsList = useContext(StandardsContext);
  const { control, getValues, handleSubmit, watch } = useForm({
    defaultValues,
    mode: "all",
    reValidateMode: "onBlur",
  });
  watch("documentSubject");

  const [checkedGridRows, setCheckedGridRows] = useState(
    defaultValues.standards || [],
  );
  const documentSubjects = useMemo(() => {
    return Array.from(
      new Set(standardsList.map(({ documentSubject }) => documentSubject)),
    )
      .sort()
      .map((label) => ({ label, value: label }));
  }, []);

  const handleClose = () => {
    onClose();
  };
  const handleModalSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };
  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setCheckedGridRows(selectedRows);
  };

  const onSubmit = ({ order }) => {
    let results;
    if (action === "edit") {
      results = {
        humanCodingScheme: selectedStandard.humanCodingScheme,
        order,
      };
    } else if (action === "search") {
      results = checkedGridRows.map(
        ({ humanCodingScheme }) => humanCodingScheme,
      );
    } else {
      results = checkedGridRows.map(({ humanCodingScheme }) => ({
        humanCodingScheme,
        order,
      }));
    }
    onClose(results);
  };

  const rows = getValues("documentSubject")
    ? standardsList
        .filter(
          ({ documentSubject }) =>
            documentSubject === getValues("documentSubject"),
        )
        .map((standard) => ({ ...standard, id: standard.humanCodingScheme }))
    : [];
  const dataGridProps = {
    checkboxSelection: true,
    columns: [
      {
        field: "humanCodingScheme",
        flex: 0,
        headerName: "Standard Name",
        width: 350,
      },
      { field: "fullStatement", flex: 1, headerName: "Description" },
    ],
    isRowSelectable: ({ row: { humanCodingScheme } }) =>
      !selectedStandards.includes(humanCodingScheme),
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
    pageSizeOptions: [100, 500, 1000],
  };

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open}>
      <DialogTitle>{_.capitalize(action)} Standards</DialogTitle>
      <DialogContent dividers={true} sx={{ display: "flex", height: 500 }}>
        <form
          id="standardsForm"
          onSubmit={handleModalSubmit}
          style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <ControlledFormSelect
                      control={control}
                      fullWidth={true}
                      label="Standard Type and Content"
                      name="documentSubject"
                      options={documentSubjects}
                      rules={{
                        required: action === "edit" ? false : true,
                      }}
                      disabled={action === "edit"}
                    />
                  ),
                  key: `standards-documentSubject`,
                },
                {
                  children: (action !== "search" && (
                    <ControlledFormSelect
                      control={control}
                      disablePlaceholder={true}
                      fullWidth={true}
                      label="Standard Order"
                      name="order"
                      options={[
                        { label: "Primary", value: "primary" },
                        { label: "Secondary", value: "secondary" },
                        { label: "Tertiary", value: "tertiary" },
                      ]}
                      rules={{
                        required: true,
                      }}
                    />
                  )) || <Box />,
                  key: "standards-order",
                },
              ],
            ]}
          />
          {getValues("documentSubject") && (
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                margin: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                }}
              >
                <DataGrid {...dataGridProps}></DataGrid>
              </Box>
            </Paper>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="text">
          Cancel
        </Button>
        <Button
          disabled={
            ((action === "add" || action === "search") &&
              !checkedGridRows.length) ||
            (action !== "search" && !getValues("order"))
          }
          color="primary"
          form="standardsForm"
          type="submit"
          variant="contained"
        >
          {action === "edit" ? "Update" : _.capitalize(action)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { StandardsDialog };
